import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/* ─────────────────────────────────────────
   Page Wrapper & Container
───────────────────────────────────────── */

export const ProcessingWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 64px);
  background: ${({ theme }) => theme.landing.background};
  transition: background 0.3s ease;
`;

export const ProcessingContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  margin-top: 64px;
  padding: ${({ theme }) => theme.landingSpacing.xl};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.lg}) {
    padding: ${({ theme }) => theme.landingSpacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
`;

/* ─────────────────────────────────────────
   Header
───────────────────────────────────────── */

export const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.landingSpacing.xl};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.landingSpacing.md};
  }
`;

export const SectionTitle = styled.h1`
  font-weight: 500;
  font-size: 1.75rem;
  line-height: 1.2;
  margin: 0;
  color: ${({ theme }) => theme.landing.text};
  letter-spacing: -0.025em;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    font-size: 1.75rem;
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 1.5rem;
  }
`;

export const SectionDescription = styled.p`
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 4px 0 0 0;
  padding: 0;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 0.95rem;
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    flex-direction: column;
    width: 100%;
  }
`;

export const DashboardButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.landing.text};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};

  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
    border-color: ${({ theme }) => theme.landing.text};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    flex: 1;
    text-align: center;
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
    padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
    font-size: 0.95rem;
  }
`;

export const SaveResultsButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  background: ${({ theme }) => theme.landing.buttonBg};
  color: #fff;
  border: 1px solid ${({ theme }) => theme.landing.buttonBg};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};

  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.landing.buttonHover};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    flex: 1;
    justify-content: center;
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
    padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
    font-size: 0.95rem;
  }
`;

/* ─────────────────────────────────────────
   Main Content Layout
───────────────────────────────────────── */

export const ContentLayout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: ${({ theme }) => theme.landingSpacing.lg};
  align-items: stretch;
  height: calc(100vh - 220px);
  min-height: 420px;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.lg}) {
    grid-template-columns: 280px 1fr;
    gap: ${({ theme }) => theme.landingSpacing.md};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    grid-template-columns: 1fr;
    height: auto;
    gap: ${({ theme }) => theme.landingSpacing.md};
  }
`;

/* ─────────────────────────────────────────
   Uploaded Documents Panel (Left)
───────────────────────────────────────── */

export const DocumentsPanel = styled.div`
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.lg};
  padding: ${({ theme }) => theme.landingSpacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.md};
  height: fit-content;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    height: auto;
    max-height: 480px;
  }
`;

export const PanelTitle = styled.h2`
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.text};
  margin: 0 0 ${({ theme }) => theme.landingSpacing.sm} 0;
  flex-shrink: 0;
`;

export const DocumentsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.xs};
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.landing.cardBorder};
    border-radius: 3px;
  }
`;

export const DocumentItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  padding: ${({ theme }) => theme.landingSpacing.sm};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
  }
`;

export const DocIconWrapper = styled.div`
  width: 36px;
  height: 36px;
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.08);
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  color: #ef4444;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const DocInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const DocName = styled.p`
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.text};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DocSize = styled.span`
  font-size: 0.775rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.textSecondary};
`;

export const DocActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.xs};
`;

export const DocActionButton = styled.button`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  color: ${({ theme }) => theme.landing.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
    color: ${({ theme }) => theme.landing.text};
  }

  &.delete:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }

  &:active {
    transform: scale(0.95);
  }
`;

/* LAYOUT FIX: flex-shrink: 0 keeps this pinned below the scrolling list,
   outside the scroll area, so it's always visible — never scrolls away. */
export const UploadMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.landingSpacing.xs};
  width: 100%;
  padding: ${({ theme }) => theme.landingSpacing.md};
  background: transparent;
  color: ${({ theme }) => theme.landing.text};
  border: 1px dashed ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};

  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: ${({ theme }) => theme.landingSpacing.xs};
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }

  &:active {
    transform: scale(0.98);
  }
`;

/* ─────────────────────────────────────────
   Results Panel (Right) — Tabs
───────────────────────────────────────── */

export const ResultsPanel = styled.div`
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.lg};
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

export const TabsBar = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.landing.cardBorder};
  padding: 0 ${({ theme }) => theme.landingSpacing.lg};
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: 0 ${({ theme }) => theme.landingSpacing.md};
  }
`;

export const Tab = styled.button`
  position: relative;
  background: transparent;
  border: none;
  padding: ${({ theme }) => theme.landingSpacing.md} ${({ theme }) => theme.landingSpacing.sm};
  margin-right: ${({ theme }) => theme.landingSpacing.md};
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  font-size: 0.95rem;
  line-height: 1.4;
  color: ${({ $active, theme }) =>
    $active ? theme.landing.buttonBg : theme.landing.textSecondary};
  cursor: pointer;
  transition: color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme.landing.buttonBg};
    border-radius: 2px 2px 0 0;
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transition: opacity 0.2s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.landing.buttonBg};
  }
`;

export const TabContent = styled.div`
  padding: ${({ theme }) => theme.landingSpacing.lg};
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
`;

/* ─────────────────────────────────────────
   Shared Tab Content Wrapper
───────────────────────────────────────── */

export const TabContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.md};
`;

/* ─────────────────────────────────────────
   Extraction Styles
───────────────────────────────────────── */

export const ExtractionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.landingSpacing.sm};
`;

export const ExtractionTitle = styled.h3`
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.text};
  margin: 0;
`;

export const ExtractedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${({ theme }) => theme.landing.iconBg};
  color: ${({ theme }) => theme.landing.text};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: 4px ${({ theme }) => theme.landingSpacing.sm};
  font-weight: 500;
  font-size: 0.8rem;
  line-height: 1.4;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const ExtractionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const ExtractionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.xs};
  padding: ${({ theme }) => theme.landingSpacing.lg} 0;
  border-bottom: 1px solid ${({ theme }) => theme.landing.cardBorder};

  &:last-child {
    border-bottom: none;
  }
`;

export const ItemSource = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const ItemSourceDoc = styled.span`
  font-weight: 500;
  font-size: 0.75rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.textSecondary};
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

export const ItemSourceDot = styled.span`
  color: ${({ theme }) => theme.landing.textSecondary};
  font-size: 0.75rem;
`;

export const ItemSourcePage = styled.span`
  font-weight: 600;
  font-size: 0.75rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.buttonBg};
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

export const ItemTitle = styled.h4`
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.text};
  margin: 0;
`;

export const ItemBody = styled.p`
  font-weight: 400;
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 0;
`;

export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 3rem;
  font-size: 14px;
  color: var(--color-text-secondary);

  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

export const EmptyExtractionState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 3rem 2rem;
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 14px;
`;

export const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 8px 12px;
  border-radius: var(--border-radius-md);
  background: ${({ $type }) =>
    $type === 'error' ? 'var(--color-background-danger)' : 'var(--color-background-info)'};
  color: ${({ $type }) =>
    $type === 'error' ? 'var(--color-text-danger)' : 'var(--color-text-info)'};
  border: 0.5px solid
    ${({ $type }) =>
      $type === 'error' ? 'var(--color-border-danger)' : 'var(--color-border-info)'};
`;

export const ExtractButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
  background: transparent;
  color: ${({ theme }) => theme.landing.text};
  border: 1px solid ${({ theme }) => theme.landing.buttonBg};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  cursor: pointer;
  transition: background 0.15s;
  margin-top: 4px;

  &:hover:not(:disabled) {
    background: var(--color-background-secondary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;
export const ExtractionFieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
`;

export const ExtractionField = styled.div`
  background: var(--color-background-primary);
  border: 0.5px solid var(--color-border-tertiary);
  border-radius: var(--border-radius-md);
  padding: 10px 12px;
`;

export const FieldLabel = styled.p`
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-tertiary);
  margin: 0 0 4px;
`;

export const FieldValue = styled.p`
  font-size: 13px;
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;
export const GlobalError = styled.div`
  background: var(--color-background-danger);
  border: 0.5px solid var(--color-border-danger);
  border-radius: var(--border-radius-md);
  padding: 10px 14px;
  font-size: 13px;
  color: var(--color-text-danger);
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AnalysisItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.landingSpacing.md};
`;

export const AnalysisItemCard = styled.div`
  background: ${({ theme }) => theme.landing.background};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.landing.buttonBg};
  }
`;

export const AnalysisItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const AnalysisItemTitle = styled.span`
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.text};
`;

export const AnalysisScoreBadge = styled.span`
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 2px 10px;
  border-radius: 20px;
  background: ${({ $score }) =>
    $score >= 80
      ? 'rgba(99,153,34,0.12)'
      : $score >= 50
        ? 'rgba(239,159,39,0.12)'
        : 'rgba(226,75,74,0.12)'};
  color: ${({ $score }) => ($score >= 80 ? '#639922' : $score >= 50 ? '#854F0B' : '#A32D2D')};
`;

export const AnalysisProgressTrack = styled.div`
  height: 5px;
  background: ${({ theme }) => theme.landing.cardBorder};
  border-radius: 99px;
  overflow: hidden;
`;

export const AnalysisProgressBar = styled.div`
  height: 100%;
  width: ${({ $score }) => $score}%;
  border-radius: 99px;
  background: ${({ $score }) => ($score >= 80 ? '#639922' : $score >= 50 ? '#EF9F27' : '#E24B4A')};
  transition: width 0.4s ease;
`;

export const AnalysisItemResponse = styled.p`
  font-size: 0.8rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 0;
`;

// ─── Extraction Tabs (sticky document-name nav) ───────────────────────────────

export const ExtractionTabsWrapper = styled.div`
  position: sticky;
  top: -16px;
  z-index: 10;
  background: ${({ theme }) => theme.landing.cardBg};
  border-bottom: 1px solid ${({ theme }) => theme.landing.cardBorder};
  padding: 0 1rem;
  display: flex;
  align-items: flex-end;
  gap: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ExtractionTabItem = styled.button`
  flex-shrink: 0;
  position: relative;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  color: ${({ $active, theme }) => ($active ? theme.landing.linkColor : theme.landing.textMuted)};
  background: transparent;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease;
  outline: none;
  overflow-y: auto;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${({ $active, theme }) => ($active ? theme.landing.linkColor : 'transparent')};
    transition: background 0.15s ease;
  }

  &:hover {
    color: ${({ $active, theme }) => ($active ? theme.landing.linkColor : theme.landing.text)};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.landing.linkColor};
    outline-offset: -2px;
    border-radius: 4px;
  }
`;
